import api from '@api';

const getters = {
  getIsAuthenticated(state) {
    return state.isAuthenticated;
  },
  getUser(state) {
    return state.user;
  },
  isStudent: (state) => {
    return state.user && state.user.data && state.user.data.roles 
      ? state.user.data.roles.some(role => role.name === 'student') 
      : false;
  },
  isTeacher: (state) => {
    return state.user && state.user.data && state.user.data.roles 
      ? state.user.data.roles.some(role => role.name === 'teacher') 
      : false;
  },
  isAdministrator: (state) => {
    return state.user && state.user.data && state.user.data.roles 
      ? state.user.data.roles.some(role => role.name === 'administrator') 
      : false;
  },
  isAttendant: (state) => {
    return state.user && state.user.data && state.user.data.roles 
      ? state.user.data.roles.some(role => role.name === 'attendant') 
      : false;
  },
  getPermissions(state) {
    return state.permissions;
  },
  getToken(state) {
    return state.userToken;
  },
  getIsRfidLogin(state) {
    return state.isRfidLogin;
  },
  getRevalidationToken(state) {
    return state.revalidationToken;
  },
  getIsGlobalLoading(state) {
    return state.isGlobalLoading;
  }
};

const actions = {
  auth({ commit }) {
    commit('AUTH_REQUEST');
    return new Promise((resolve, reject) => {
      api.auth.auth().then(({ data }) => {
        resolve(data);
        commit('AUTH_SUCCESS', data);
      }).catch((e) => {
        console.error('[Vuex auth] auth action FAILED:', e);
        commit('AUTH_FAILURE');
        reject(e);
      });
    });
  },
  authEclass({ commit }, { code }) {
    commit('LOGIN_REQUEST');
    return new Promise((resolve, reject) => {
      api.auth.authEclass(code).then(({ data }) => {
        resolve(data);
        commit('LOGIN_SUCCESS', data);
      }).catch((e) => {
        commit('LOGIN_FAILURE');
        reject(e);
      });
    });
  },
  permissions({ commit }) {
    commit('PERMISSIONS_REQUEST');
    return new Promise((resolve, reject) => {
      api.auth.permissions().then((response) => {
        resolve(response);
        commit('PERMISSIONS_SUCCESS', response);
      }).catch((e) => {
        console.error('[Vuex auth] permissions action FAILED:', e);
        commit('PERMISSIONS_FAILURE');
        reject(e);
      });
    });
  },
  login({ commit }, { email, password }) {
    commit('LOGIN_REQUEST');
    return new Promise((resolve, reject) => {
      api.auth.login({ email, password }).then((response) => {
        resolve(response);
        commit('LOGIN_SUCCESS', response);
      }).catch((e) => {
        commit('LOGIN_FAILURE');
        reject(e);
      });
    });
  },
  logout({ commit, state }) {
    const wasRfidLogin = state.isRfidLogin;
    commit('LOGOUT_REQUEST');
    return new Promise((resolve, reject) => {
      api.auth.logout().then(({ data }) => {
        // REMOVE FLAG LOGIC
        /*
        if (wasRfidLogin) {
            localStorage.setItem('lastLogoutWasRfid', 'true');
        } else {
            localStorage.removeItem('lastLogoutWasRfid');
        }
        */

        // Clear other local storage and commit mutations
        localStorage.removeItem('user-token');
        localStorage.removeItem('isRfidLogin');
        localStorage.removeItem('isAuthenticated'); 
        commit('LOGOUT_SUCCESS'); 
        
        resolve(wasRfidLogin); 
      }).catch((e) => {
        // Clear local state/storage on failure
        localStorage.removeItem('user-token');
        localStorage.removeItem('isRfidLogin');
        localStorage.removeItem('isAuthenticated');
        // No need to remove lastLogoutWasRfid here, as it wasn't set
        commit('LOGOUT_FAILURE'); 
        reject(e); 
      });
    });
  },
  register({ commit }, payload) {
    commit('REGISTER_REQUEST');
    return new Promise((resolve, reject) => {
      api.auth.register(payload).then((response) => {
        resolve(response);
        commit('REGISTER_SUCCESS', response.access_token);
      }).catch((e) => {
        commit('REGISTER_FAILURE');
        reject(e);
      });
    });
  },
  rfidLogin({ commit }, { rfid }) {
    commit('LOGIN_REQUEST');
    return new Promise((resolve, reject) => {
        api.auth.rfidLogin({ rfid })
            .then((response) => {
                const token = response.access_token || response.data?.access_token;
                if (!token) {
                   console.error('[Vuex auth] No access_token found in rfidLogin response:', response);
                   commit('LOGIN_FAILURE');
                   return reject(new Error('No access token received'));
                }
                commit('RFID_LOGIN_SUCCESS', token);
                resolve(response);
            })
            .catch((e) => {
                console.error('[Vuex auth] rfidLogin action FAILED:', e);
                commit('LOGIN_FAILURE');
                reject(e);
            });
    });
  },
  resumeSession({ commit }, { revalidation_token }) {
    commit('AUTH_REQUEST');
    return new Promise((resolve, reject) => {
        if (!revalidation_token) {
            commit('AUTH_FAILURE');
            return reject(new Error('Missing revalidation token'));
        }
        const originalRevalToken = revalidation_token;

        api.auth.resumeSession({ revalidation_token: originalRevalToken })
            .then((response) => {
                const token = response.access_token || response.data?.access_token;
                const user = response.user || response.data?.user;
                if (!token || !user) {
                    console.error('[Vuex auth] Invalid resumeSession response:', response);
                    commit('AUTH_FAILURE');
                    return reject(new Error('Invalid resume session response'));
                }
                commit('LOGIN_SUCCESS', { 
                    access_token: token, 
                    revalidation_token: originalRevalToken
                }); 
                commit('AUTH_SUCCESS', user); 
                resolve(response);
            })
            .catch((err) => {
                console.error('[Vuex auth] resumeSession action FAILED:', err); 
                commit('AUTH_FAILURE'); 
                reject(err);
            });
    });
  }
};

const mutations = {
  AUTH_REQUEST() { 
  },
  AUTH_SUCCESS(state, payload) {
    state.isAuthenticated = true;
    localStorage.setItem('isAuthenticated', 'true');
    state.user = { ...payload };
  },
  AUTH_FAILURE(state) {
    state.isAuthenticated = false;
    localStorage.removeItem('isAuthenticated');
  },
  LOGIN_REQUEST() { 
   },
  LOGIN_SUCCESS(state, payload) {
    state.userToken = payload.access_token;
    state.isAuthenticated = true; 
    localStorage.setItem('user-token', payload.access_token);
    localStorage.setItem('isAuthenticated', 'true'); 
    localStorage.removeItem('isRfidLogin'); 
    localStorage.removeItem('lastLogoutWasRfid'); 

    // Store or clear revalidation token based on payload
    if (payload.revalidation_token) {
        state.revalidationToken = payload.revalidation_token;
        sessionStorage.setItem('revalidation-token', payload.revalidation_token);
    } else {
        state.revalidationToken = null;
        sessionStorage.removeItem('revalidation-token');
    }
    state.isRfidLogin = false;
  },
  LOGIN_FAILURE(state) {
    state.userToken = null;
    state.isAuthenticated = false;
    state.isRfidLogin = false;
    localStorage.removeItem('user-token');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('isRfidLogin');
  },
  LOGOUT_REQUEST() { },
  LOGOUT_SUCCESS(state) {
    state.userToken = null;
    state.user = {};
    state.permissions = {};
    state.isAuthenticated = false;
    state.isRfidLogin = false;
    state.revalidationToken = null;
    localStorage.removeItem('user-token');
    localStorage.removeItem('isRfidLogin');
    localStorage.removeItem('isAuthenticated');
    sessionStorage.removeItem('revalidation-token');
  },
  LOGOUT_FAILURE(state) {
    state.userToken = null;
    state.user = {};
    state.permissions = {};
    state.isAuthenticated = false;
    state.isRfidLogin = false;
    state.revalidationToken = null;
    localStorage.removeItem('user-token');
    localStorage.removeItem('isRfidLogin');
    localStorage.removeItem('isAuthenticated');
    sessionStorage.removeItem('revalidation-token');
  },
  REGISTER_REQUEST() { },
  REGISTER_SUCCESS(state, token) {
    state.userToken = token;
    localStorage.setItem('user-token', token);
  },
  REGISTER_FAILURE() {
    localStorage.removeItem('isAuthenticated');
  },
  PERMISSIONS_REQUEST() { 
  },
  PERMISSIONS_SUCCESS(state, data) {
    state.permissions = data;
  },
  PERMISSIONS_FAILURE() {
  },
  RFID_LOGIN_SUCCESS(state, token) {
    state.userToken = token;
    state.isRfidLogin = true;
    state.isAuthenticated = true; 
    localStorage.setItem('user-token', token);
    localStorage.setItem('isRfidLogin', 'true');
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.removeItem('lastLogoutWasRfid');
  },
  CLEAR_REVALIDATION_TOKEN(state) {
      state.revalidationToken = null;
      sessionStorage.removeItem('revalidation-token');
  },
  SET_GLOBAL_LOADING(state, value) {
    state.isGlobalLoading = value;
  },
  RESTORE_AUTH_STATE(state, { wasAuthenticated, wasRfidLogin }) {
    state.isAuthenticated = wasAuthenticated;
    state.isRfidLogin = wasRfidLogin;
    if (wasAuthenticated) {
      localStorage.setItem('isAuthenticated', 'true');
      if (wasRfidLogin) {
        localStorage.setItem('isRfidLogin', 'true');
      }
    }
  }
};

const state = {
  userToken: localStorage.getItem('user-token') || null,
  isAuthenticated: localStorage.getItem('isAuthenticated') === 'true',
  isRfidLogin: localStorage.getItem('isRfidLogin') === 'true',
  revalidationToken: sessionStorage.getItem('revalidation-token') || null,
  user: {},
  permissions: {},
  isGlobalLoading: false,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
